import React, { useMemo, useState } from 'react'
import {
	Avatar,
	Box,
	Dialog,
	IconButton,
	MenuItem,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import TableRowsIcon from '@mui/icons-material/TableRows'
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline'
import { type GridColDef } from '@mui/x-data-grid-pro'
import { AnimatedDataGridPro, useDataGridAnimations, type DensityMode } from '../../CustomDataGrid'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs, { Dayjs } from 'dayjs'
import CustomButton from '../../CustomButton/CustomButton'
import CustomColumnMenu, { columnMenuPopperSx } from '../../CustomDataGrid/CustomColumnMenu'
import { popupActionButtonSx, popupSecondaryButtonSx } from '../popupButtonStyles'
import { calendarInputSx, calendarPopperSlotProps, popupInputLabelSx } from '../popupInputStyles'
import { useModal } from '../../../../../store/ModalProvider'
import type { OvertimeModalData, OvertimeRow } from '../../../../../types/modal'

// Invisible but clickable opener so picker still opens when clicked
const EmptyButton: React.ComponentType<any> = (props: any) => (
	<IconButton
		{...props}
		sx={{ ...(props?.sx || {}), opacity: 0 }}
	/>
)

const dialogShadow =
	'0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)'

const inputSx = {
	'& .MuiInputLabel-root': {
		...popupInputLabelSx,
	},
	'& .MuiOutlinedInput-root': {
		backgroundColor: '#FFFFFF',
		borderRadius: '4px',
		minHeight: '40px',
		fontSize: '13px',
		fontWeight: 500,
		color: '#092056',
		'& fieldset': {
			borderColor: 'rgba(134, 149, 175, 0.3)',
		},
		'&:hover fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.4)',
		},
		'&.Mui-focused fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.6)',
		},
	},
	'& .MuiOutlinedInput-input': {
		padding: '8px 12px',
		lineHeight: 1.3,
	},
	'& .MuiSelect-select': {
		padding: '8px 36px 8px 12px',
		display: 'flex',
		alignItems: 'center',
	},
	'& .MuiSelect-icon': {
		color: '#00A5FF',
		right: '10px',
	},
}

const toggleGroupSx = {
	border: '1px solid rgba(134, 149, 175, 0.15)',
	borderRadius: '4px',
	'& .MuiToggleButton-root': {
		border: 'none',
		borderRadius: 0,
		padding: '4.8px',
		'&:not(:last-child)': {
			borderRight: '0.6px solid rgba(134, 149, 175, 0.15)',
		},
		'&.Mui-selected': {
			backgroundColor: '#F5F7FC',
			'&:hover': {
				backgroundColor: '#F5F7FC',
			},
		},
	},
}

const tableSx = {
	border: 'none',
	'--DataGrid-rowBorderColor': 'transparent',
	'& .MuiDataGrid-columnHeaders': {
		backgroundColor: '#FFFFFF',
		borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
		borderTop: 'none',
		boxShadow: 'none',
	},
	'& .MuiDataGrid-columnHeaderTitle': {
		fontFamily: 'Poppins, sans-serif',
		fontWeight: 600,
		fontSize: '12px',
		color: '#8695AF',
		letterSpacing: '0.17px',
		lineHeight: '24px',
		textTransform: 'none',
	},
	'& .MuiDataGrid-columnSeparator': {
		opacity: 0,
		color: 'rgba(134, 149, 175, 0.6)',
		transition: 'opacity 0.2s ease',
	},
	'& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator': {
		opacity: 1,
	},
	'& .MuiDataGrid-columnSeparator--resizable': {
		cursor: 'col-resize',
	},
	'& .MuiDataGrid-columnHeader': {
		borderRight: 'none',
	},
	'& .MuiDataGrid-cell': {
		fontFamily: 'Poppins, sans-serif',
		fontSize: '12px',
		color: '#092056',
		borderBottom: 'none',
		borderRight: 'none',
	},
	'& .MuiDataGrid-row': {
		borderBottom: 'none',
	},
	'& .MuiDataGrid-row:hover': {
		backgroundColor: 'rgba(0, 165, 255, 0.06)',
	},
	'& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
		outline: 'none',
	},
}

const defaultRows: OvertimeRow[] = [
	{
		id: 'ot-1',
		motorista: 'Alexandre Gomes',
		nr: '0137',
		data: '02/05/2024',
		horasExtra: '02:30',
		servico: 'Transfer',
		observacoes: 'Atraso na saída',
		viatura: '0062',
		cliente: 'Best Transfers 4 U',
	},
	{
		id: 'ot-2',
		motorista: 'Ana Martins',
		nr: '0210',
		data: '02/05/2024',
		horasExtra: '01:45',
		servico: 'Excursão',
		observacoes: 'Cliente VIP',
		viatura: '0046',
		cliente: 'Transol Corporate',
	},
	{
		id: 'ot-3',
		motorista: 'Rui Silva',
		nr: '0182',
		data: '02/05/2024',
		horasExtra: '03:10',
		servico: 'Turismo',
		observacoes: 'Trânsito',
		viatura: '0021',
		cliente: 'Frota Azul',
	},
]

const CustomModalOvertimeHours: React.FC = () => {
	const { isOpen, closeModal, modalData, modalHandlers } = useModal()
	const data = modalData as OvertimeModalData | undefined
	const [handleExport, handlePrint] = modalHandlers ?? []

	const [startDate, setStartDate] = useState<Dayjs | null>(
		data?.startDate ? dayjs(data.startDate, 'DD/MM/YYYY') : dayjs('02/05/2024', 'DD/MM/YYYY'),
	)
	const [endDate, setEndDate] = useState<Dayjs | null>(
		data?.endDate ? dayjs(data.endDate, 'DD/MM/YYYY') : dayjs('02/05/2024', 'DD/MM/YYYY'),
	)
	const [empresa, setEmpresa] = useState(data?.empresa ?? 'Transol')
	const [filial, setFilial] = useState(data?.filial ?? 'Transol')
	const motoristaOptions =
		data?.motoristas?.length && data.motoristas.length > 0
			? data.motoristas
			: ['Todos', 'Alexandre Gomes', 'Ana Martins', 'Rui Silva']
	const [motorista, setMotorista] = useState<string>(data?.motorista ?? motoristaOptions[0])
	const [density, setDensity] = useState<DensityMode>('compact')

	// Use our custom animation hook
	const { animateRows } = useDataGridAnimations(isOpen)

	const columns = useMemo<GridColDef<OvertimeRow>[]>(
		() => [
			{
				field: 'motorista',
				headerName: 'Motorista',
				minWidth: 190,
				flex: 1,
				renderCell: params => {
					const name = String(params.row.motorista ?? '')
					const initial = name.trim().charAt(0) || 'M'
					return (
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
							<Avatar
								src={params.row.avatarUrl}
								sx={{
									borderRadius: '100%',
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
									justifyContent: 'center',
									bgcolor: 'lightgrey 50% / cover no-repeat',
									width: 32,
									height: 32,
									fontSize: '20px',
									fontWeight: 400,
									lineHeight: 1,
								}}
							>
								{initial}
							</Avatar>
							<Typography sx={{ fontSize: '12px', lineHeight: 1.3 }}>{name}</Typography>
						</Box>
					)
				},
			},
			{ field: 'nr', headerName: 'Nr', width: 60 },
			{ field: 'data', headerName: 'Data', width: 100 },
			{ field: 'horasExtra', headerName: 'Horas Extra', width: 110 },
			{ field: 'servico', headerName: 'Serviço', minWidth: 120, flex: 1 },
			{ field: 'cliente', headerName: 'Cliente', minWidth: 140 },
			{ field: 'viatura', headerName: 'Viatura', width: 90 },
			{
				field: 'observacoes',
				headerName: 'Observações',
				minWidth: 180,
				flex: 1.2,
			},
		],
		[],
	)

	if (!data) {
		return null
	}

	const rows = data?.rows?.length ? data.rows : defaultRows

	const handleClearFilters = () => {
		setStartDate(dayjs('02/05/2024', 'DD/MM/YYYY'))
		setEndDate(dayjs('02/05/2024', 'DD/MM/YYYY'))
		setEmpresa('Transol')
		setFilial('Transol')
		setMotorista(motoristaOptions[0])
	}

	const handleExportRows = () => {
		if (typeof handleExport === 'function') {
			handleExport(rows)
			return
		}
		console.log('Exportar horas extra', rows)
	}

	const handlePrintRows = () => {
		if (typeof handlePrint === 'function') {
			handlePrint(rows)
			return
		}
		console.log('Imprimir horas extra', rows)
	}

	return (
		<Dialog
			open={isOpen}
			onClose={closeModal}
			maxWidth={false}
			sx={{ pointerEvents: 'auto', zIndex: 2000 }}
			PaperProps={{
				sx: {
					width: 'min(1200px, 96vw)',
					borderRadius: '6px',
					backgroundColor: '#FFFFFF',
					boxShadow: dialogShadow,
					overflow: 'hidden',
					zIndex: 2001,
				},
			}}
		>
			<Box sx={{ fontFamily: 'Poppins, sans-serif', color: '#092056' }}>
				<Box
					sx={{
						padding: '16px 24px 0',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: '16px',
					}}
				>
					<Typography sx={{ fontSize: '16px', fontWeight: 600 }}>Horas Extra</Typography>
					<IconButton
						aria-label='Fechar'
						onClick={closeModal}
						sx={{ padding: '4px' }}
					>
						<CloseRoundedIcon sx={{ fontSize: 20, color: '#00A5FF' }} />
					</IconButton>
				</Box>

				<Box sx={{ padding: '16px 24px 0', display: 'grid', gap: '16px' }}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<Box
							sx={{
								display: 'flex',
								flexWrap: 'wrap',
								gap: '12px',
								alignItems: 'flex-end',
								justifyContent: 'space-between',
							}}
						>
							<Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
								<DatePicker
									label='Data Inicial'
									value={startDate}
									onChange={value => setStartDate(value)}
									format='DD/MM/YYYY'
									slots={{ openPickerButton: EmptyButton }}
									slotProps={{
										textField: { sx: [inputSx, calendarInputSx] },
										...calendarPopperSlotProps,
									}}
								/>
								<DatePicker
									label='Data Final'
									value={endDate}
									onChange={value => setEndDate(value)}
									format='DD/MM/YYYY'
									slots={{ openPickerButton: EmptyButton }}
									slotProps={{
										textField: { sx: [inputSx, calendarInputSx] },
										...calendarPopperSlotProps,
									}}
								/>
								<TextField
									select
									label='Empresa'
									value={empresa}
									onChange={event => setEmpresa(event.target.value)}
									size='small'
									sx={{ ...inputSx, width: 200 }}
									SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
								>
									<MenuItem value='Transol'>Transol</MenuItem>
								</TextField>
								<TextField
									select
									label='Filial'
									value={filial}
									onChange={event => setFilial(event.target.value)}
									size='small'
									sx={{ ...inputSx, width: 200 }}
									SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
								>
									<MenuItem value='Transol'>Transol</MenuItem>
								</TextField>
								<TextField
									select
									label='Motorista'
									value={motorista}
									onChange={event => setMotorista(event.target.value)}
									size='small'
									sx={{ ...inputSx, width: 220 }}
									SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
								>
									{motoristaOptions.map(option => (
										<MenuItem
											key={option}
											value={option}
										>
											{option}
										</MenuItem>
									))}
								</TextField>
							</Box>
							<CustomButton
								variant='text'
								onClick={handleClearFilters}
								sx={{
									fontSize: '14px',
									fontWeight: 600,
									lineHeight: '22px',
									color: '#00A5FF',
									padding: 0,
									minWidth: 'auto',
									textTransform: 'none',
									'&:hover': { backgroundColor: 'transparent' },
								}}
							>
								Limpar Filtros
							</CustomButton>
						</Box>
					</LocalizationProvider>

					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							flexWrap: 'wrap',
							gap: '12px',
						}}
					>
						<Typography sx={{ fontSize: '20px', fontWeight: 600, color: '#092056' }}>
							{rows.length} Motoristas com Horas Extra
						</Typography>
						<ToggleButtonGroup
							value={density}
							exclusive
							onChange={(_, value) => value && setDensity(value)}
							size='small'
							sx={toggleGroupSx}
						>
							<ToggleButton
								value='compact'
								aria-label='densidade compacta'
							>
								<TableRowsIcon
									sx={{
										width: '14.4px',
										height: '14.4px',
										color: density === 'compact' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
									}}
								/>
							</ToggleButton>
							<ToggleButton
								value='standard'
								aria-label='densidade espaçada'
							>
								<ViewHeadlineIcon
									sx={{
										width: '14.4px',
										height: '14.4px',
										color: density === 'standard' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
									}}
								/>
							</ToggleButton>
						</ToggleButtonGroup>
					</Box>

					<AnimatedDataGridPro
						slots={{ columnMenu: CustomColumnMenu }}
						slotProps={{ basePopper: { sx: columnMenuPopperSx } }}
						rows={rows}
						columns={columns}
						density={density}
						columnHeaderHeight={40}
						disableRowSelectionOnClick
						animateRows={animateRows}
						pagination={true}
						pageSize={10}
						sx={[tableSx]}
					/>
				</Box>

				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-end',
						gap: '8px',
						padding: '16px 24px',
					}}
				>
					<CustomButton
						variant='text'
						onClick={closeModal}
						sx={popupSecondaryButtonSx}
					>
						Fechar
					</CustomButton>
					<CustomButton
						variant='text'
						onClick={handleExportRows}
						sx={popupSecondaryButtonSx}
					>
						Exportar
					</CustomButton>
					<CustomButton
						variant='contained'
						color='primary'
						onClick={handlePrintRows}
						sx={popupActionButtonSx}
					>
						Imprimir
					</CustomButton>
				</Box>
			</Box>
		</Dialog>
	)
}

export default CustomModalOvertimeHours
